import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const nav = useNavigate();
  const [mode, setMode] = useState("theme");
  const [items, setItems] = useState([]);

  const load = () => api.get("/admin/puzzles", { params: { mode } }).then((r) => setItems(r.data || []));
  useEffect(() => { load(); /* eslint-disable-line */ }, [mode]);

  const del = async (id) => {
    if (!window.confirm("Delete this puzzle?")) return;
    await api.delete(`/admin/puzzles/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-red-500 mb-1">Backstage</div>
          <h1 className="font-display text-3xl sm:text-5xl font-black uppercase">Admin</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => nav("/admin/wrestlers")}
            className="border-slate-700 text-slate-200 hover:bg-slate-800"
            data-testid="admin-wrestlers-link">
            Manage Wrestlers
          </Button>
          <Button onClick={() => nav(`/admin/new/${mode}`)} data-testid="admin-new-btn"
            className="bg-red-600 hover:bg-red-500 text-white font-bold uppercase">
            <Plus className="h-4 w-4 mr-1" /> New {mode === "theme" ? "Theme" : "Promo"}
          </Button>
        </div>
      </div>

      <Tabs value={mode} onValueChange={setMode} className="mt-6">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="theme" data-testid="admin-tab-theme">Entrance Themes</TabsTrigger>
          <TabsTrigger value="promo" data-testid="admin-tab-promo">Promos</TabsTrigger>
        </TabsList>
        <TabsContent value={mode} className="mt-4">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/70 overflow-hidden" data-testid="admin-list">
            <div className="grid grid-cols-12 px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-800">
              <div className="col-span-2">Date</div>
              <div className="col-span-4">Answer</div>
              <div className="col-span-2">Era</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {items.length === 0 ? (
              <div className="p-6 text-slate-500" data-testid="admin-empty">No puzzles yet. Click New to add one.</div>
            ) : items.map((p) => (
              <div key={p.id} className="grid grid-cols-12 items-center px-4 py-3 border-b border-slate-800/60"
                   data-testid={`admin-row-${p.id}`}>
                <div className="col-span-2 font-mono text-sm">{p.scheduled_date || "—"}</div>
                <div className="col-span-4 font-display font-bold truncate">{p.answer || "(unnamed)"}</div>
                <div className="col-span-2 text-sm text-slate-400">{p.era || "—"}</div>
                <div className="col-span-2">
                  <span className={`text-[10px] px-2 py-1 rounded font-mono uppercase ${
                    p.status === "published" ? "bg-emerald-950/50 text-emerald-400 border border-emerald-800" :
                    "bg-slate-800 text-slate-400 border border-slate-700"}`}>
                    {p.status}
                  </span>
                </div>
                <div className="col-span-2 flex justify-end gap-1">
                  <Link to={`/admin/puzzle/${p.id}`}>
                    <Button size="sm" variant="ghost" data-testid={`edit-${p.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Button size="sm" variant="ghost" onClick={() => del(p.id)} data-testid={`del-${p.id}`}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
